import type { useTranslations } from 'next-intl'
import type { ReactNode } from 'react'

import { emphasiseVerb } from './StepLabel'
import type { ToolCategory, ToolCategoryCounts } from './turnModel'

type Translator = ReturnType<typeof useTranslations<'features.AssistantV2'>>

/** Whether the run is over ("Ran 6 queries") or still going ("Running 6 queries"). */
export type RunTense = 'past' | 'live'

type Clause = {
  /** The sentence-initial form, whose verb the locale marks for emphasis. */
  initial: (count: number) => ReactNode
  continuing: (count: number) => string
}

const CATEGORY_ORDER: readonly ToolCategory[] = ['query', 'docs', 'dashboard', 'other']

/**
 * The summary line of a tool run, such as "Ran 6 queries, searched docs twice" or, while the run is live,
 * "Running 6 queries, searching docs twice": one clause per category that has calls, in the order query, docs,
 * dashboard, other. The locale supplies each clause in a sentence-initial and a continuing form, so only the first
 * clause is capitalised and carries the emphasised verb.
 */
export default function toolRunSummary(counts: ToolCategoryCounts, tense: RunTense, t: Translator): ReactNode {
  const clauses = tense === 'live' ? liveClauses(t) : pastClauses(t)
  const present = CATEGORY_ORDER.flatMap((category) => {
    const count = counts[category]
    return count ? [{ count, clause: clauses[category] }] : []
  })
  const [first, ...rest] = present
  if (!first) return null

  return (
    <>
      {first.clause.initial(first.count)}
      {rest.map(({ count, clause }) => `, ${clause.continuing(count)}`).join('')}
    </>
  )
}

function pastClauses(t: Translator): Record<ToolCategory, Clause> {
  return {
    query: {
      initial: (count) => t.rich('runQueryFirst', { count, verb: emphasiseVerb }),
      continuing: (count) => t('runQuery', { count })
    },
    docs: {
      initial: (count) => t.rich('runDocsFirst', { count, verb: emphasiseVerb }),
      continuing: (count) => t('runDocs', { count })
    },
    dashboard: {
      initial: (count) => t.rich('runDashboardFirst', { count, verb: emphasiseVerb }),
      continuing: (count) => t('runDashboard', { count })
    },
    other: {
      initial: (count) => t.rich('runOtherFirst', { count, verb: emphasiseVerb }),
      continuing: (count) => t('runOther', { count })
    }
  }
}

function liveClauses(t: Translator): Record<ToolCategory, Clause> {
  return {
    query: {
      initial: (count) => t.rich('runLiveQueryFirst', { count, verb: emphasiseVerb }),
      continuing: (count) => t('runLiveQuery', { count })
    },
    docs: {
      initial: (count) => t.rich('runLiveDocsFirst', { count, verb: emphasiseVerb }),
      continuing: (count) => t('runLiveDocs', { count })
    },
    dashboard: {
      initial: (count) => t.rich('runLiveDashboardFirst', { count, verb: emphasiseVerb }),
      continuing: (count) => t('runLiveDashboard', { count })
    },
    other: {
      initial: (count) => t.rich('runLiveOtherFirst', { count, verb: emphasiseVerb }),
      continuing: (count) => t('runLiveOther', { count })
    }
  }
}
